/**
 * ProgressViewContext (iter74 Phase B)
 *
 * Which "shape" is the student currently reading their progress in — the
 * cartoon Simple Roadmap or the Advanced Analytics with the Athlete Card
 * + radar? This is a single global choice so /dashboard and /progress
 * always agree, and it round-trips to the user profile via
 * POST /api/me/progress-view.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);
const STORAGE_KEY = "rally_progress_view";
const VALID = new Set(["simple", "advanced"]);
const DEFAULT_VIEW = "advanced";

function loadInitial() {
  if (typeof window === "undefined") return DEFAULT_VIEW;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return VALID.has(v) ? v : DEFAULT_VIEW;
  } catch { return DEFAULT_VIEW; }
}

export function ProgressViewProvider({ children }) {
  const { user } = useAuth();
  const [view, setViewState] = useState(loadInitial);
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current) return;
    const pref = user?.progress_view;
    if (pref && VALID.has(pref)) {
      hydrated.current = true;
      setViewState(pref);
      try { window.localStorage.setItem(STORAGE_KEY, pref); } catch { /* ignore */ }
    } else if (user) {
      hydrated.current = true;
    }
  }, [user]);

  const setView = useCallback(async (next) => {
    if (!VALID.has(next)) return;
    setViewState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    try { await api.post("/me/progress-view", { view: next }); } catch { /* ignore */ }
  }, []);

  const value = useMemo(() => ({ view, setView }), [view, setView]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useProgressView() {
  const ctx = useContext(Ctx);
  if (!ctx) return { view: DEFAULT_VIEW, setView: () => {} };
  return ctx;
}
